import {
  useMediaQuery,
  WhmButton,
  WhmCheckbox,
  WhmDialog,
  WhmDialogActions,
  WhmDialogContent,
  WhmDialogTitle,
  WhmDivider,
  WhmFormControlLabel,
  WhmGrid,
  WhmIconButton,
  WhmInputAdornment,
  WhmLoadingButton,
  WhmStack,
  whmTheme,
  WhmTypography,
} from "@securitize/reactjs-whm";
import { Translate, TranslateHtml } from "../../../Translation/Translate";
import { I18nContext, translationKeys } from "../../../../contexts/I18nContext";
import { useContext, useId, useRef, useState } from "react";
import useUserInfo from "../../../../hooks/useUserInfo";

import { Form, Formik } from "formik";
import * as yup from "yup";
import { SnackbarContext } from "../../../shared/Snackbar";
import ErrorSnackbar from "../../../shared/ErrorSnackbar";
import { createKeyParty, updateKeyParty } from "../../../../data/api";
import { useQueryClient } from "@tanstack/react-query";
import { SID } from "@securitize/domain";
import {
  CreateKeyPartyPayloadDto,
  UboOwning,
} from "../../../../data/dtos/create-key-party.dto";
import { ResponseError } from "../../../../data/types/responseError";
import { ReactComponent as EmailIcon } from "@securitize/assets-sec-ui/dist/icons/svg/email.svg";
import { ReactComponent as CloseIcon } from "@securitize/assets-sec-ui/dist/icons/svg/exit.svg";
import { showsAsEnhanced } from "./utils";
import FormikWhmTextField from "../../../shared/FormikWhmTextField";
import { REQUIRED_GENERIC_HIDDEN_MESSAGE } from "../../../../utils/yupUtils";
import { ReactComponent as CheckMark } from "@securitize/assets-sec-ui/dist/icons/svg/check.svg";
import { KeyParty } from "../../../../data/dtos/get-key-party.dto";
import {
  allowedIndividualNameCharsRegex,
  allowedMiddleNameCharsRegex,
} from "../../../../utils/validations";

export default function AddIndividualDialog({
  open,
  keyParty,
  onClose,
  openCompleteDetails,
  readOnly = false,
}: {
  open: boolean;
  keyParty?: KeyParty;
  onClose: () => void;
  openCompleteDetails: (keyParty: KeyParty) => void;
  readOnly?: boolean;
}) {
  const user = useUserInfo();
  const enhanced = showsAsEnhanced(user);
  const snackbar = useContext(SnackbarContext);
  const addIndividualFormId = useId();
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery(whmTheme.breakpoints.down("xl"));
  const { getTranslation } = useContext(I18nContext);
  const [displayTopDivider, setDisplayTopDivider] = useState(false);
  const [displayBottomDivider, setDisplayBottomDivider] = useState(false);

  const dialogContentRef = useRef<HTMLDivElement>(null);

  return (
    <WhmDialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth={isMobile ? "xl" : "md"}
      scroll="paper"
      TransitionProps={{
        onEnter: () => {
          if (dialogContentRef.current) {
            if (
              dialogContentRef.current.scrollHeight >
              dialogContentRef.current.clientHeight
            ) {
              setDisplayBottomDivider(true);
            }
          }
        },
      }}
    >
      <Formik
        initialValues={{
          signerType: "individual" as SID.InvestorType,
          individualName: {
            firstName: "",
            middleName: "",
            lastName: "",
          },
          email: "",
          uboOwning: "none" as UboOwning,
          isLegalSigner: false,
          ...keyParty,
        }}
        onSubmit={async (values, helpers) => {
          helpers.setSubmitting(true);
          try {
            if (keyParty?.id) {
              const response = await updateKeyParty(keyParty.id, values);

              await queryClient.invalidateQueries({
                predicate: (query) =>
                  ["key-parties", "onboarding", "pending-actions"].includes(
                    query.queryKey[0] as string,
                  ),
              });

              snackbar.showSnackbar({
                severity: "info",
                message: (
                  <WhmStack direction="row" alignItems="center" gap="16px">
                    <CheckMark fill="white" />
                    <Translate
                      translationKey={
                        translationKeys.PROFILE_KEY_PARTIES_UPDATE_INDIVIDUAL_SUCCESS_MESSAGE
                      }
                      replacements={{
                        name: response.displayName,
                      }}
                    />
                  </WhmStack>
                ),
              });
            } else {
              const response = await createKeyParty(
                values as CreateKeyPartyPayloadDto,
              );

              await queryClient.invalidateQueries({
                predicate: (query) =>
                  ["key-parties", "onboarding", "pending-actions"].includes(
                    query.queryKey[0] as string,
                  ),
              });

              snackbar.showSnackbar({
                severity: "info",
                message: (
                  <WhmStack direction="row" alignItems="center" gap="16px">
                    <CheckMark fill="white" />

                    <Translate
                      translationKey={
                        translationKeys.PROFILE_KEY_PARTIES_ADD_INDIVIDUAL_SUCCESS_MESSAGE
                      }
                      replacements={{
                        name: response.displayName,
                      }}
                    />

                    <WhmButton
                      variant="outlined"
                      size="small"
                      sx={{
                        color: whmTheme.palette.common.white,
                        borderColor: whmTheme.palette.common.white,
                        width: { xs: "100%", xl: "auto" },
                        minWidth: "fit-content !important",
                        "&:hover": {
                          borderColor: whmTheme.palette.common.white,
                        },
                      }}
                      onClick={() => openCompleteDetails(response)}
                    >
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_ADD_INDIVIDUAL_SUCCESS_COMPLETE_DETAILS_BUTTON
                        }
                      />
                    </WhmButton>
                  </WhmStack>
                ),
              });
            }

            onClose();
          } catch (e) {
            console.error(e);
            snackbar.showSnackbar({
              severity: "error",
              message: <ErrorSnackbar error={e as ResponseError} />,
            });
          } finally {
            helpers.setSubmitting(false);
          }
        }}
        validationSchema={yup.object().shape({
          individualName: yup.object().shape({
            firstName: yup
              .string()
              .min(
                2,
                getTranslation(
                  translationKeys.REGISTRATION_VALIDATION_MINIMUM_LENGTH,
                ),
              )
              .max(
                32,
                getTranslation(
                  translationKeys.REGISTRATION_VALIDATION_MAXIMUM_LENGTH,
                ),
              )
              .required(REQUIRED_GENERIC_HIDDEN_MESSAGE)
              .matches(allowedIndividualNameCharsRegex, {
                message: getTranslation(
                  translationKeys.REGISTRATION_VALIDATION_INVALID_NAME,
                ),
                excludeEmptyString: true,
              }),
            middleName: yup
              .string()
              .min(
                2,
                getTranslation(
                  translationKeys.REGISTRATION_VALIDATION_MINIMUM_LENGTH,
                ),
              )
              .max(
                32,
                getTranslation(
                  translationKeys.REGISTRATION_VALIDATION_MAXIMUM_LENGTH,
                ),
              )
              .matches(allowedMiddleNameCharsRegex, {
                message: getTranslation(
                  translationKeys.REGISTRATION_VALIDATION_INVALID_NAME,
                ),
                excludeEmptyString: true,
              }),
            lastName: yup
              .string()
              .min(
                2,
                getTranslation(
                  translationKeys.REGISTRATION_VALIDATION_MINIMUM_LENGTH,
                ),
              )
              .max(
                32,
                getTranslation(
                  translationKeys.REGISTRATION_VALIDATION_MAXIMUM_LENGTH,
                ),
              )
              .required(REQUIRED_GENERIC_HIDDEN_MESSAGE)
              .matches(allowedIndividualNameCharsRegex, {
                message: getTranslation(
                  translationKeys.REGISTRATION_VALIDATION_INVALID_NAME,
                ),
                excludeEmptyString: true,
              }),
          }),
          email: yup
            .string()
            .email(
              getTranslation(
                translationKeys.PROFILE_KEY_PARTIES_VALIDATION_EMAIL,
              ),
            )
            .required(REQUIRED_GENERIC_HIDDEN_MESSAGE),
        })}
      >
        {(formik) => (
          <>
            <WhmDialogTitle variant="h4">
              {keyParty?.id ? (
                <Translate
                  translationKey={
                    translationKeys.PROFILE_KEY_PARTIES_UPDATE_INDIVIDUAL_TITLE
                  }
                />
              ) : (
                <Translate
                  translationKey={
                    translationKeys.PROFILE_KEY_PARTIES_ADD_INDIVIDUAL_TITLE
                  }
                />
              )}
            </WhmDialogTitle>
            <WhmIconButton
              onClick={onClose}
              sx={{
                position: "absolute",
                right: "8px",
                top: "8px",
              }}
            >
              <CloseIcon />
            </WhmIconButton>
            {displayTopDivider && <WhmDivider />}
            <WhmDialogContent
              ref={dialogContentRef}
              onScroll={(e) => {
                const { scrollTop, scrollHeight, offsetHeight } =
                  e.currentTarget;

                setDisplayTopDivider(scrollTop !== 0);
                setDisplayBottomDivider(
                  scrollTop !== scrollHeight - offsetHeight,
                );
              }}
            >
              <Form id={addIndividualFormId}>
                <WhmStack gap="16px">
                  <WhmTypography>
                    <Translate
                      translationKey={
                        translationKeys.PROFILE_KEY_PARTIES_ADD_INDIVIDUAL_DESCRIPTION
                      }
                    />
                  </WhmTypography>
                  <WhmStack>
                    <WhmTypography variant="subtitle1">
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_PERSONAL_INFORMATION_TITLE
                        }
                      />
                    </WhmTypography>
                    <WhmTypography variant="body2" color="text.secondary">
                      <TranslateHtml
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_PERSONAL_INFORMATION_DESCRIPTION
                        }
                      />
                    </WhmTypography>
                  </WhmStack>
                  {!formik.isValid &&
                    formik.submitCount > 0 &&
                    (formik.values.individualName.firstName === "" ||
                      formik.values.individualName.lastName === "" ||
                      formik.values.email === "") && (
                      <WhmTypography variant="body2" color="error">
                        <Translate
                          translationKey={
                            translationKeys.PROFILE_KEY_PARTIES_VALIDATION_ERROR
                          }
                        />
                      </WhmTypography>
                    )}
                  <WhmGrid container columns={{ xs: 6, md: 12 }} spacing="16px">
                    <WhmGrid item xs={6}>
                      <FormikWhmTextField
                        label={
                          <Translate
                            translationKey={
                              translationKeys.PROFILE_KEY_PARTIES_FIRST_NAME_LABEL
                            }
                          />
                        }
                        fullWidth
                        name="individualName.firstName"
                        error={
                          formik.touched.individualName?.firstName &&
                          (formik.errors.individualName?.firstName ===
                          REQUIRED_GENERIC_HIDDEN_MESSAGE
                            ? formik.submitCount > 0
                            : Boolean(formik.errors.individualName?.firstName))
                        }
                        disabled={readOnly || formik.isSubmitting}
                      />
                    </WhmGrid>
                    <WhmGrid item xs={6}>
                      <FormikWhmTextField
                        label={
                          <Translate
                            translationKey={
                              translationKeys.PROFILE_KEY_PARTIES_MIDDLE_NAME_LABEL
                            }
                          />
                        }
                        fullWidth
                        name="individualName.middleName"
                        error={
                          formik.touched.individualName?.middleName &&
                          (formik.errors.individualName?.middleName ===
                          REQUIRED_GENERIC_HIDDEN_MESSAGE
                            ? formik.submitCount > 0
                            : Boolean(formik.errors.individualName?.middleName))
                        }
                        disabled={readOnly || formik.isSubmitting}
                      />
                    </WhmGrid>
                  </WhmGrid>
                  <WhmGrid container columns={{ xs: 6, md: 12 }} spacing="16px">
                    <WhmGrid item xs={6}>
                      <FormikWhmTextField
                        label={
                          <Translate
                            translationKey={
                              translationKeys.PROFILE_KEY_PARTIES_LAST_NAME_LABEL
                            }
                          />
                        }
                        fullWidth
                        name="individualName.lastName"
                        error={
                          formik.touched.individualName?.lastName &&
                          (formik.errors.individualName?.lastName ===
                          REQUIRED_GENERIC_HIDDEN_MESSAGE
                            ? formik.submitCount > 0
                            : Boolean(formik.errors.individualName?.lastName))
                        }
                        disabled={readOnly || formik.isSubmitting}
                      />
                    </WhmGrid>
                    <WhmGrid item xs={6}>
                      <FormikWhmTextField
                        placeholder={getTranslation(
                          translationKeys.PROFILE_KEY_PARTIES_EMAIL_LABEL,
                        )}
                        label={
                          <Translate
                            translationKey={
                              translationKeys.PROFILE_KEY_PARTIES_EMAIL_LABEL
                            }
                          />
                        }
                        InputProps={{
                          startAdornment: formik.values.email ? (
                            <WhmInputAdornment position="start">
                              <EmailIcon
                                width={24}
                                height={24}
                                fill={whmTheme.palette.action.active}
                              />
                            </WhmInputAdornment>
                          ) : undefined,
                        }}
                        fullWidth
                        name="email"
                        error={
                          formik.touched.email &&
                          (formik.errors.email ===
                          REQUIRED_GENERIC_HIDDEN_MESSAGE
                            ? formik.submitCount > 0
                            : Boolean(formik.errors.email))
                        }
                        disabled={readOnly || formik.isSubmitting}
                      />
                    </WhmGrid>
                  </WhmGrid>
                  <WhmDivider />
                  <WhmStack>
                    <WhmTypography variant="subtitle1">
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_RELATIONSHIP_TITLE
                        }
                      />
                    </WhmTypography>
                    <WhmTypography variant="body2" color="text.secondary">
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_RELATIONSHIP_DESCRIPTION
                        }
                      />
                    </WhmTypography>
                  </WhmStack>
                  <WhmGrid container columns={{ xs: 6, md: 12 }} spacing="16px">
                    <WhmGrid item xs={6}>
                      {enhanced ? (
                        <WhmFormControlLabel
                          disabled={readOnly || formik.isSubmitting}
                          sx={{
                            flex: 1,
                            marginRight: 0,
                            alignItems: "flex-start",
                          }}
                          control={
                            <WhmCheckbox
                              checked={formik.values.uboOwning === "10+"}
                              onChange={() => {
                                if (formik.values.uboOwning === "10+") {
                                  formik
                                    .setFieldValue("uboOwning", "none")
                                    .catch((e) => console.error(e));
                                } else {
                                  formik
                                    .setFieldValue("uboOwning", "10+")
                                    .catch((e) => console.error(e));
                                }
                              }}
                            />
                          }
                          label={
                            <WhmStack>
                              <WhmTypography>
                                <Translate
                                  translationKey={
                                    translationKeys.PROFILE_KEY_PARTIES_10_PERCENT_UBO_LABEL
                                  }
                                />
                              </WhmTypography>
                              <WhmTypography
                                variant="body2"
                                color="text.secondary"
                              >
                                <Translate
                                  translationKey={
                                    translationKeys.PROFILE_KEY_PARTIES_10_PERCENT_UBO_DESCRIPTION
                                  }
                                />
                              </WhmTypography>
                            </WhmStack>
                          }
                        />
                      ) : (
                        <WhmFormControlLabel
                          disabled={readOnly || formik.isSubmitting}
                          sx={{
                            flex: 1,
                            marginRight: 0,
                            alignItems: "flex-start",
                          }}
                          control={
                            <WhmCheckbox
                              checked={formik.values.uboOwning === "25+"}
                              onChange={() => {
                                if (formik.values.uboOwning === "25+") {
                                  formik
                                    .setFieldValue("uboOwning", "none")
                                    .catch((e) => console.error(e));
                                } else {
                                  formik
                                    .setFieldValue("uboOwning", "25+")
                                    .catch((e) => console.error(e));
                                }
                              }}
                            />
                          }
                          label={
                            <WhmStack>
                              <WhmTypography>
                                <Translate
                                  translationKey={
                                    translationKeys.PROFILE_KEY_PARTIES_25_PERCENT_UBO_LABEL
                                  }
                                />
                              </WhmTypography>
                              <WhmTypography
                                variant="body2"
                                color="text.secondary"
                              >
                                <Translate
                                  translationKey={
                                    translationKeys.PROFILE_KEY_PARTIES_25_PERCENT_UBO_DESCRIPTION
                                  }
                                />
                              </WhmTypography>
                            </WhmStack>
                          }
                        />
                      )}
                    </WhmGrid>
                    <WhmGrid item xs={6}>
                      <WhmFormControlLabel
                        disabled={readOnly || formik.isSubmitting}
                        sx={{
                          flex: 1,
                          marginRight: 0,
                          alignItems: "flex-start",
                        }}
                        control={
                          <WhmCheckbox
                            checked={formik.values.isLegalSigner}
                            onChange={() => {
                              formik
                                .setFieldValue(
                                  "isLegalSigner",
                                  !formik.values.isLegalSigner,
                                )
                                .catch((e) => console.error(e));
                            }}
                          />
                        }
                        label={
                          <WhmStack>
                            <WhmTypography>
                              <Translate
                                translationKey={
                                  translationKeys.PROFILE_KEY_PARTIES_LEGAL_SIGNER_LABEL
                                }
                              />
                            </WhmTypography>
                            <WhmTypography
                              variant="body2"
                              color="text.secondary"
                            >
                              <Translate
                                translationKey={
                                  translationKeys.PROFILE_KEY_PARTIES_LEGAL_SIGNER_DESCRIPTION
                                }
                              />
                            </WhmTypography>
                          </WhmStack>
                        }
                      />
                    </WhmGrid>
                  </WhmGrid>
                </WhmStack>
              </Form>
            </WhmDialogContent>
            {displayBottomDivider && <WhmDivider />}
            <WhmDialogActions>
              <WhmButton variant="outlined" color="secondary" onClick={onClose}>
                <Translate
                  translationKey={translationKeys.PROFILE_KEY_PARTIES_CANCEL}
                />
              </WhmButton>
              {!readOnly && (
                <WhmLoadingButton
                  variant="contained"
                  type="submit"
                  data-test-id="add-individual-submit-button"
                  form={addIndividualFormId}
                  loading={formik.isSubmitting}
                  sx={{
                    svg: {
                      display: "block !important",
                    },
                    "&.MuiLoadingButton-loading": {
                      color: "black",
                      opacity: "0.3",
                      span: {
                        color: "black",
                      },
                    },
                  }}
                >
                  {keyParty?.id &&
                    (formik.isSubmitting ? (
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_UPDATE_INDIVIDUAL_SUBMIT_IN_PROGRESS
                        }
                      />
                    ) : (
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_UPDATE_INDIVIDUAL_SUBMIT
                        }
                      />
                    ))}
                  {!keyParty?.id &&
                    (formik.isSubmitting ? (
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_ADD_INDIVIDUAL_SUBMIT_IN_PROGRESS
                        }
                      />
                    ) : (
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_ADD_INDIVIDUAL_SUBMIT
                        }
                      />
                    ))}
                </WhmLoadingButton>
              )}
            </WhmDialogActions>
          </>
        )}
      </Formik>
    </WhmDialog>
  );
}
